import React from 'react';
import { Texture } from 'three';

import { isDefined } from '../../../../../../utils/variables';
import { CesiumPointsMaterial } from '../../../../../../viewer/geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { PointCloudClassification } from './point-cloud-classifications';

const getColor = (classification: PointCloudClassification) => {
  if (!isDefined(CesiumPointsMaterial.instance.imageData)) {
    return 'gray';
  }

  const index = Array.isArray(classification.pointClass)
    ? classification.pointClass[0]
    : classification.pointClass;
  const red = CesiumPointsMaterial.instance.imageData.data[index * 4 + 0];
  const green = CesiumPointsMaterial.instance.imageData.data[index * 4 + 1];
  const blue = CesiumPointsMaterial.instance.imageData.data[index * 4 + 2];
  return `rgb(${red},${green},${blue})`;
};

const isVisible = (classification: PointCloudClassification) => {
  return CesiumPointsMaterial.instance.isClassificationVisible(
    Array.isArray(classification.pointClass)
      ? classification.pointClass[0]
      : classification.pointClass,
  );
};

const usePointCloudClassifications = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
  const [context, setContext] = React.useState<CanvasRenderingContext2D>();

  React.useEffect(() => {
    const context = canvasRef.current?.getContext('2d');

    if (!canvasRef.current || !context) {
      throw new Error('Canvas context is missing.');
    }

    setContext(context);

    const { width, height } = CesiumPointsMaterial.instance.material.uniforms.texture1.value
      .image as HTMLImageElement;
    canvasRef.current.width = width;
    canvasRef.current.height = height;

    if (CesiumPointsMaterial.instance.imageData) {
      context.putImageData(CesiumPointsMaterial.instance.imageData, 0, 0);
    } else {
      context.drawImage(
        CesiumPointsMaterial.instance.material.uniforms.texture1.value.image as CanvasImageSource,
        0,
        0,
      );
      CesiumPointsMaterial.instance.imageData = context.getImageData(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
    }
  }, [canvasRef]);

  const toggleVisibility = React.useCallback(
    (classification: PointCloudClassification) => {
      if (Array.isArray(classification.pointClass)) {
        classification.pointClass.forEach((x) =>
          CesiumPointsMaterial.instance.toggleClassVisibility(x),
        );
      } else {
        CesiumPointsMaterial.instance.toggleClassVisibility(classification.pointClass);
      }

      if (canvasRef && context && CesiumPointsMaterial.instance.imageData) {
        context.putImageData(CesiumPointsMaterial.instance.imageData, 0, 0);
        const canvasTexture = new Texture(canvasRef.current!);
        CesiumPointsMaterial.instance.setTexture(canvasTexture);
      }
    },
    [canvasRef, context],
  );

  return { loaded: isDefined(context), isVisible, getColor, toggleVisibility };
};

export { usePointCloudClassifications };
